import { Box, Typography } from '@mui/material';
import { IconLibrary } from '@product-framework/index';
import { useLocalization } from '@ui-framework/index';
import IssueStatusFilterButton from '../IssueStatusFilterButton';
import {
  END_STATE_STATUS_CODES,
  STATUS_COLOR,
  UNSUPPORTED_STATUS_CODES,
} from '../constants';

const getStatusButtonColor = statusCode => {
  if (!statusCode) {
    return 'primary';
  }

  const color = STATUS_COLOR[statusCode];
  return color && color !== 'default' ? color : 'inherit';
};

const getStatusIcon = statusCode =>
  IconLibrary[`ISSUE_STATUS_${statusCode || 'ALL'}`] || IconLibrary.Issue;

function IssueStatusSummary({
  statuses,
  statusCounts,
  selectedStatusCode,
  onStatusChange,
  excludedStatusCodes,
}) {
  const { formatMessage } = useLocalization();
  // Quick filters cover in-process statuses only; end-state statuses
  // (Resolved, Cancelled) are reachable through the Status field in the
  // search form, and unsupported statuses (Blocked, Closed, Duplicate)
  // are never offered.
  const excludedCodes = new Set([
    ...(excludedStatusCodes?.length
      ? excludedStatusCodes
      : END_STATE_STATUS_CODES),
    ...UNSUPPORTED_STATUS_CODES,
  ]);
  const statusOptions = statuses
    .filter(status => !excludedCodes.has(status.code))
    .map(status => ({
      ...status,
      count: statusCounts[status.code] || 0,
    }));

  return (
    <Box sx={{ mb: 2.25 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
        {formatMessage({
          id: 'STATUS',
          defaultMessage: 'Status',
        })}
      </Typography>
      <Box
        sx={{
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          p: 0.5,
        }}>
        {statusOptions.map(status => {
          const selected = selectedStatusCode === status.code;

          return (
            <IssueStatusFilterButton
              key={status.code || 'ALL'}
              label={status.label}
              count={status.count}
              showCount
              selected={selected}
              color={getStatusButtonColor(status.code)}
              onClick={() => onStatusChange(selected ? '' : status.code)}
              minWidth={status.label.length > 16 ? 190 : 124}
              withIcon
              Icon={getStatusIcon(status.code)}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default IssueStatusSummary;
